import type { ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { ThinkingAuraEngine } from './ThinkingAuraEngine';
import type { ThinkingAuraSettings } from './thinkingAura';

const TRAY_TRANSITION_FALLBACK_MS = 600;

interface TraySnapshot {
  key: string;
  content: ReactNode;
  hasContent: boolean;
}

/** The measured-height transition's direction is deliberately data-only so
 * every tray state can use the same path (and so its rebase rules stay tested). */
export function activityTrayTransitionDirection(fromHeight: number, toHeight: number): 'expand' | 'collapse' | 'steady' {
  if (Math.abs(toHeight - fromHeight) < 1) return 'steady';
  return toHeight > fromHeight ? 'expand' : 'collapse';
}

/** A mounted empty tray stays out of both layout and the accessibility tree. */
export function activityTrayIsVisible(hasContent: boolean, outgoingHasContent: boolean): boolean {
  return hasContent || outgoingHasContent;
}

/**
 * The tray owns composition changes only. A persistent task changing state is
 * animated by TaskLayoutTransition inside that row, so including state or kind
 * here would snapshot the entire tray and render both versions at once.
 */
export function activityTrayContentKey(hasPanel: boolean, rowKeys: string[]): string {
  return [hasPanel ? 'panel' : 'no-panel', rowKeys.join(',')].join('|');
}

/**
 * One persistent visual shell for all non-input AgentBar content. The aura is
 * outside the snapshot layers, so changing tasks, panels, and assumptions can
 * overlap during a transition without creating or restarting another mesh.
 */
export function ActivityTray({
  contentKey,
  hasContent,
  children,
  settings,
  reducedMotion,
}: {
  contentKey: string;
  hasContent: boolean;
  children: ReactNode;
  settings: ThinkingAuraSettings;
  reducedMotion: boolean;
}): ReactElement {
  const latestRef = useRef<TraySnapshot>({ key: contentKey, content: children, hasContent });
  const [renderedKey, setRenderedKey] = useState(contentKey);
  const [outgoing, setOutgoing] = useState<TraySnapshot | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  const incomingRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const next = { key: contentKey, content: children, hasContent };
    if (contentKey !== renderedKey) {
      const previous = latestRef.current;
      latestRef.current = next;
      setOutgoing(previous);
      setRenderedKey(contentKey);
      return;
    }
    latestRef.current = next;
  }, [children, contentKey, hasContent, renderedKey]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!outgoing || !host) return undefined;
    outgoingRef.current?.setAttribute('inert', '');
    if (reducedMotion) {
      setOutgoing(null);
      host.style.height = '';
      host.style.overflow = '';
      host.style.transition = '';
      delete host.dataset.layoutDirection;
      return undefined;
    }

    const currentHeight = host.getBoundingClientRect().height;
    // Measure the layers' layout boxes, not scrollHeight. A freshly added row
    // enters with translateY(10px); scrollHeight includes that visual overflow
    // and makes the tray animate past its true resting height before snapping
    // back when the temporary fixed height is removed.
    const fromHeight = host.style.height ? currentHeight : outgoingRef.current?.getBoundingClientRect().height ?? currentHeight;
    const toHeight = incomingRef.current?.getBoundingClientRect().height ?? fromHeight;
    host.dataset.layoutDirection = activityTrayTransitionDirection(fromHeight, toHeight);
    host.style.transition = 'none';
    host.style.height = `${fromHeight}px`;
    host.style.overflow = 'hidden';
    host.getBoundingClientRect();
    const frame = window.requestAnimationFrame(() => {
      host.style.transition = '';
      host.style.height = `${toHeight}px`;
    });
    let settled = false;
    let timeout = 0;
    const settle = () => {
      if (settled) return;
      settled = true;
      host.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(timeout);
      setOutgoing(null);
      host.style.height = '';
      host.style.overflow = '';
      host.style.transition = '';
      delete host.dataset.layoutDirection;
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      // The task layout inside the tray also transitions its height. Ignore
      // that bubbled event and wait for this host's own measured-height move.
      if (event.target === host && event.propertyName === 'height') settle();
    };
    host.addEventListener('transitionend', onTransitionEnd);
    // A nearly steady height may not emit transitionend. Keep a generous
    // fallback, measured from before the requestAnimationFrame, for that case.
    timeout = window.setTimeout(settle, TRAY_TRANSITION_FALLBACK_MS);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      host.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [outgoing, reducedMotion, renderedKey]);

  // `renderedKey` still names the visible snapshot between render and the
  // layout-effect handoff. Including it prevents a one-frame radius/background
  // drop when a populated tray begins its exit.
  const outgoingHasContent = outgoing?.hasContent ?? (contentKey !== renderedKey && latestRef.current.hasContent);
  const visible = activityTrayIsVisible(hasContent, outgoingHasContent);

  return (
    <section className={`composer-activity-tray${visible ? ' composer-activity-tray-active' : ''}`} aria-label="Agent activity" aria-hidden={visible ? undefined : true}>
      <ThinkingAuraEngine settings={settings} reducedMotion={reducedMotion} />
      <div ref={hostRef} className={`activity-tray-transition${outgoing ? ' activity-tray-transition-active' : ''}`}>
        {outgoing ? (
          <div ref={outgoingRef} className="activity-tray-layer activity-tray-layer-exit" aria-hidden="true">
            {outgoing.content}
          </div>
        ) : null}
        <div ref={incomingRef} key={renderedKey} className={`activity-tray-layer ${outgoing ? 'activity-tray-layer-enter' : 'activity-tray-layer-stable'}`}>
          {children}
        </div>
      </div>
    </section>
  );
}
